import React, { useMemo } from 'react';
import { 
  Mail, Phone, Globe, Link, Facebook, Twitter, Linkedin, Instagram, Youtube 
} from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { templateStyles, templateSizes } from '../../data/mockData';

// TikTok icon
const TikTokIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
  </svg>
);

const socialIcons = {
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
  instagram: Instagram,
  youtube: Youtube,
  tiktok: TikTokIcon,
};

const SignaturePreview = ({ data, onCopyFormatted, onCopyHTML }) => {
  // Debugging: log preview values when rendering to ensure URLs are set
  try {
    // Only log in development
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('SignaturePreview render', {
        logoPreview: data.logoPreview,
        bannerPreview: data.bannerPreview,
        logoLocalPreview: data.logoLocalPreview,
        bannerLocalPreview: data.bannerLocalPreview,
      });
    }
  } catch (e) {
    // ignore
  }
  const getTextStyle = (styles) => {
    return {
      fontWeight: styles?.bold ? 'bold' : 'normal',
      fontStyle: styles?.italic ? 'italic' : 'normal',
      textDecoration: styles?.underline ? 'underline' : 'none',
    };
  };

  const getSizeStyles = () => {
    switch (data.templateSize) {
      case 'pequeno':
        return { nameFontSize: '14px', textFontSize: '11px', iconSize: 14, padding: '12px' };
      case 'grande':
        return { nameFontSize: '20px', textFontSize: '14px', iconSize: 20, padding: '24px' };
      default: // medio
        return { nameFontSize: '16px', textFontSize: '12px', iconSize: 16, padding: '16px' };
    }
  };

  const sizeStyles = getSizeStyles();

  const activeSocials = useMemo(() => {
    return ['facebook', 'twitter', 'linkedin', 'instagram', 'youtube', 'tiktok']
      .filter(key => data[key])
      .map(key => ({ key, url: data[key], Icon: socialIcons[key] }));
  }, [data]);

  const getTemplateStyles = () => {
    const base = {
      wrapper: {},
      container: {},
      leftBorder: true,
      logoPosition: 'left',
      divider: false,
    };

    switch (data.templateStyle) {
      case 'minimalista':
        return { ...base, leftBorder: false, divider: true };
      case 'criativo':
        return { ...base, leftBorder: false, logoPosition: 'top' };
      case 'compacto':
        return { ...base, leftBorder: false };
      case 'moderno':
        return { ...base, leftBorder: true, divider: true };
      case 'profissional':
        return { ...base, leftBorder: false, divider: true, logoPosition: 'right' };
      case 'elegante':
        return { ...base, leftBorder: false, divider: true };
      case 'negrito':
        return { ...base, leftBorder: true };
      default: // classico
        return base;
    }
  };

  const templateConfig = getTemplateStyles();

  const renderSignature = () => {
    const hasContent = data.fullName || data.position || data.company || data.email || data.phone || data.website;
    
    if (!hasContent) {
      return (
        <div className="text-center py-12 text-slate-400">
          <p>Preencha os campos para visualizar sua assinatura</p>
        </div>
      );
    }

    return (
      <div
        className="signature-preview"
        style={{
          fontFamily: data.fontFamily,
          backgroundColor: data.backgroundColor,
          padding: sizeStyles.padding,
          borderLeft: templateConfig.leftBorder ? `4px solid ${data.primaryColor}` : 'none',
          display: 'flex',
          flexDirection: templateConfig.logoPosition === 'top' ? 'column' : 'row',
          gap: '16px',
          alignItems: templateConfig.logoPosition === 'top' ? 'center' : 'flex-start',
        }}
      >
        {/* Logo Left/Top */}
        {data.logoPreview && (templateConfig.logoPosition === 'left' || templateConfig.logoPosition === 'top') && (
          <div style={{ flexShrink: 0 }}>
            <img
              src={data.logoPreview}
              alt="Logo"
              style={{
                width: data.templateSize === 'pequeno' ? '50px' : data.templateSize === 'grande' ? '80px' : '64px',
                height: 'auto',
                borderRadius: `${data.logoBorderRadius}%`,
              }}
              onError={(e) => {
                if (data.logoLocalPreview) {
                  e.currentTarget.src = data.logoLocalPreview;
                }
              }}
            />
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1 }}>
          {/* Name */}
          {data.fullName && (
            <div
              style={{
                color: data.primaryColor,
                fontSize: sizeStyles.nameFontSize,
                marginBottom: '4px',
                ...getTextStyle(data.fullNameStyles),
              }}
            >
              {data.fullName}
            </div>
          )}

          {/* Position & Company */}
          {(data.position || data.company) && (
            <div
              style={{
                color: data.secondaryColor,
                fontSize: sizeStyles.textFontSize,
                marginBottom: '8px',
              }}
            >
              {data.position && (
                <span style={getTextStyle(data.positionStyles)}>{data.position}</span>
              )}
              {data.position && data.company && ' | '}
              {data.company && (
                <span style={getTextStyle(data.companyStyles)}>{data.company}</span>
              )}
            </div>
          )}

          {templateConfig.divider && (
            <div
              style={{
                height: '1px',
                backgroundColor: data.secondaryColor,
                opacity: 0.3,
                margin: '8px 0',
              }}
            />
          )}

          {/* Contact Info */}
          <div
            style={{
              color: data.secondaryColor,
              fontSize: sizeStyles.textFontSize,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              marginBottom: '8px',
            }}
          >
            {data.email && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Mail size={sizeStyles.iconSize - 2} />
                {data.email}
              </span>
            )}
            {data.phone && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Phone size={sizeStyles.iconSize - 2} />
                {data.phone}
              </span>
            )}
            {data.website && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Globe size={sizeStyles.iconSize - 2} />
                {data.website}
              </span>
            )}
          </div>

          {/* Social Links */}
          {activeSocials.length > 0 && (
            <div
              style={{
                display: 'flex',
                gap: '8px',
                marginTop: '8px',
              }}
            >
              {activeSocials.map(({ key, Icon }) => (
                <Icon
                  key={key}
                  style={{ color: data.accentColor }}
                  className={`w-${sizeStyles.iconSize === 14 ? 3 : sizeStyles.iconSize === 20 ? 5 : 4} h-${sizeStyles.iconSize === 14 ? 3 : sizeStyles.iconSize === 20 ? 5 : 4}`}
                />
              ))}
            </div>
          )}

          {/* Custom Links */}
          {data.customLinks.filter(l => l.label && l.url).length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px',
                marginTop: '8px',
                fontSize: sizeStyles.textFontSize,
              }}
            >
              {data.customLinks
                .filter(l => l.label && l.url)
                .map((link) => (
                  <span
                    key={link.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: data.accentColor,
                    }}
                  >
                    <Link size={sizeStyles.iconSize - 2} />
                    {link.label}
                  </span>
                ))}
            </div>
          )}
        </div>

        {/* Logo Right */}
        {data.logoPreview && templateConfig.logoPosition === 'right' && (
          <div style={{ flexShrink: 0 }}>
            <img
              src={data.logoPreview}
              alt="Logo"
              style={{
                width: data.templateSize === 'pequeno' ? '50px' : data.templateSize === 'grande' ? '80px' : '64px',
                height: 'auto',
                borderRadius: `${data.logoBorderRadius}%`,
              }}
              onError={(e) => {
                if (data.logoLocalPreview) {
                  e.currentTarget.src = data.logoLocalPreview;
                }
              }}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Copy Buttons */}
      <div className="flex gap-2 justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={onCopyFormatted}
          className="text-sm"
        >
          Copiar Formatado
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onCopyHTML}
          className="text-sm"
        >
          Copiar HTML
        </Button>
      </div>

      {/* Template Style */}
      <div className="space-y-3">
        <Label className="text-slate-700 font-medium">Estilo do Template</Label>
        <RadioGroup
          value={data.templateStyle}
          onValueChange={(value) => data.onChange?.({ ...data, templateStyle: value })}
          className="grid grid-cols-2 gap-2"
        >
          {templateStyles.map((style) => (
            <div key={style.value} className="flex items-center space-x-2">
              <RadioGroupItem value={style.value} id={style.value} />
              <Label htmlFor={style.value} className="cursor-pointer text-sm">
                {style.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Template Size */}
      <div className="space-y-3">
        <Label className="text-slate-700 font-medium">Tamanho do Template</Label>
        <div className="flex gap-2">
          {templateSizes.map((size) => (
            <Button
              key={size.value}
              variant={data.templateSize === size.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => data.onChange?.({ ...data, templateSize: size.value })}
              className="flex-1"
            >
              {size.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Preview Area */}
      <div className="space-y-3">
        <Label className="text-slate-700 font-medium">Visualização</Label>
        <div className="border border-slate-200 rounded-lg p-4 bg-white shadow-inner">
          {renderSignature()}
        </div>
      </div>

      {/* Banner */}
      {data.bannerPreview && (
        <div className="mt-4">
          <img
            src={data.bannerPreview}
            alt="Banner"
            style={{
              width: '100%',
              borderRadius: `${data.bannerBorderRadius}%`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default SignaturePreview;
